import { useState, useCallback, useRef } from 'react';
import * as duckdb from '@duckdb/duckdb-wasm';

let dbInstance = null;
let connInstance = null;
let initPromise = null;

async function initDB() {
  if (dbInstance) return { db: dbInstance, conn: connInstance };
  if (initPromise) return initPromise;

  initPromise = (async () => {
    const JSDELIVR_BUNDLES = duckdb.getJsDelivrBundles();
    const bundle = await duckdb.selectBundle(JSDELIVR_BUNDLES);
    const worker_url = URL.createObjectURL(
      new Blob([`importScripts("${bundle.mainWorker}");`], { type: 'text/javascript' })
    );
    const worker = new Worker(worker_url);
    const logger = new duckdb.ConsoleLogger();
    const db = new duckdb.AsyncDuckDB(logger, worker);
    await db.instantiate(bundle.mainModule, bundle.pthreadWorker);
    const conn = await db.connect();
    dbInstance = db;
    connInstance = conn;
    URL.revokeObjectURL(worker_url);
    return { db, conn };
  })();

  return initPromise;
}

export function useDuckDB() {
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dbReady, setDbReady] = useState(false);
  const registeredFiles = useRef(new Set());

  const ensureDB = useCallback(async () => {
    const result = await initDB();
    setDbReady(true);
    return result;
  }, []);

  const registerCSV = useCallback(async (file) => {
    setLoading(true);
    try {
      const { db, conn } = await ensureDB();
      const tableName = file.name
        .replace(/\.csv$/i, '')
        .replace(/[^a-zA-Z0-9_]/g, '_')
        .replace(/^(\d)/, '_$1');

      const arrayBuffer = await file.arrayBuffer();
      const uint8 = new Uint8Array(arrayBuffer);
      const fileName = `${tableName}.csv`;

      await db.registerFileBuffer(fileName, uint8);
      registeredFiles.current.add(fileName);

      // Drop if exists, then create
      try { await conn.query(`DROP TABLE IF EXISTS "${tableName}"`); } catch(e) {}
      await conn.query(`CREATE TABLE "${tableName}" AS SELECT * FROM read_csv_auto('${fileName}', header=true, sample_size=10000)`);

      // Get schema
      const schemaResult = await conn.query(`DESCRIBE "${tableName}"`);
      const schemaRows = schemaResult.toArray().map(r => ({
        name: r.column_name,
        type: r.column_type,
      }));

      const countResult = await conn.query(`SELECT COUNT(*) as cnt FROM "${tableName}"`);
      const count = Number(countResult.toArray()[0].cnt);

      setTables(prev => {
        const filtered = prev.filter(t => t.name !== tableName);
        return [...filtered, { name: tableName, schema: schemaRows, rowCount: count, fileName: file.name }];
      });

      return tableName;
    } finally {
      setLoading(false);
    }
  }, [ensureDB]);

  const runQuery = useCallback(async (sql) => {
    const { conn } = await ensureDB();
    const result = await conn.query(sql);
    const rows = result.toArray().map(r => {
      const obj = {};
      for (const [k, v] of Object.entries(r)) {
        obj[k] = typeof v === 'bigint' ? Number(v) : v;
      }
      return obj;
    });
    const columns = result.schema.fields.map(f => f.name);
    return { rows, columns };
  }, [ensureDB]);

  return { tables, loading, dbReady, registerCSV, runQuery, ensureDB };
}
